import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner
} from 'react-bootstrap';
import {
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaShieldAlt
} from 'react-icons/fa';
import { getApiKeys, updateApiKey } from '../services/adminApiService';
import './ApiKeyManagement.css';

const ApiKeyManagement = () => {
  const [keys, setKeys] = useState([]);
  const [values, setValues] = useState({});
  const [visible, setVisible] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;

    getApiKeys()
      .then(result => {
        if (!cancelled) setKeys(result);
      })
      .catch(requestError => {
        if (!cancelled) setError(requestError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async key => {
    const value = (values[key.id] || '').trim();
    if (!value) {
      setSuccess('');
      setError(`Enter a new ${key.label} key before saving.`);
      return;
    }

    setSaving(current => ({ ...current, [key.id]: true }));
    setError('');
    setSuccess('');

    try {
      const updatedKey = await updateApiKey(key.id, value);
      setKeys(current => current.map(item => (
        item.id === updatedKey.id ? updatedKey : item
      )));
      setValues(current => ({ ...current, [key.id]: '' }));
      setVisible(current => ({ ...current, [key.id]: false }));
      setSuccess(`${key.label} was updated successfully.`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(current => ({ ...current, [key.id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="api-key-loading text-center" aria-live="polite">
        <Spinner animation="border" role="status" />
        <p className="mt-3">Loading secure configuration…</p>
      </div>
    );
  }

  return (
    <section className="api-key-management">
      <div className="api-key-hero">
        <div className="api-key-hero-icon" aria-hidden="true">
          <FaShieldAlt />
        </div>
        <div>
          <p className="api-key-eyebrow">Administrator settings</p>
          <h1>API Keys</h1>
          <p className="api-key-intro">
            Rotate integration credentials without opening a terminal. Stored keys
            are never sent back to this screen.
          </p>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          <FaCheckCircle className="me-2" aria-hidden="true" />
          {success}
        </Alert>
      )}

      <Row className="g-4">
        {keys.map(key => (
          <Col xs={12} lg={6} key={key.id}>
            <Card className="api-key-card h-100">
              <Card.Body>
                <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <span className="api-key-provider-icon" aria-hidden="true">
                      <FaKey />
                    </span>
                    <div>
                      <Card.Title className="mb-1">{key.label}</Card.Title>
                      <Card.Text className="api-key-description mb-0">
                        {key.description}
                      </Card.Text>
                    </div>
                  </div>
                  <Badge bg={key.configured ? 'success' : 'secondary'}>
                    {key.configured ? 'Configured' : 'Missing'}
                  </Badge>
                </div>

                <div className="api-key-current mb-4">
                  <span>Current credential</span>
                  <code>{key.maskedValue || 'Not configured'}</code>
                </div>

                <Form.Group controlId={`api-key-${key.id}`}>
                  <Form.Label>New key</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={visible[key.id] ? 'text' : 'password'}
                      value={values[key.id] || ''}
                      onChange={event => setValues(current => ({
                        ...current,
                        [key.id]: event.target.value
                      }))}
                      onKeyDown={event => {
                        if (event.key === 'Enter') handleSave(key);
                      }}
                      placeholder={`Paste a new ${key.label} key`}
                      autoComplete="new-password"
                      autoCapitalize="none"
                      spellCheck="false"
                      disabled={saving[key.id]}
                    />
                    <Button
                      variant="outline-light"
                      onClick={() => setVisible(current => ({
                        ...current,
                        [key.id]: !current[key.id]
                      }))}
                      aria-label={visible[key.id] ? 'Hide new key' : 'Show new key'}
                      disabled={saving[key.id]}
                    >
                      {visible[key.id] ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                  <Form.Text>
                    Saving replaces only this credential and applies it to new requests.
                  </Form.Text>
                </Form.Group>

                <Button
                  className="mt-3 w-100"
                  variant="primary"
                  onClick={() => handleSave(key)}
                  disabled={saving[key.id] || !(values[key.id] || '').trim()}
                >
                  {saving[key.id] ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Saving securely…
                    </>
                  ) : (
                    `Update ${key.label}`
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="api-key-security-note mt-4">
        <FaShieldAlt aria-hidden="true" />
        <p className="mb-0">
          Changes are written atomically to the server’s protected environment file.
          Existing values remain masked and cannot be retrieved through this panel.
        </p>
      </div>
    </section>
  );
};

export default ApiKeyManagement;
